import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { privateRequest } from "services/privateRequest";

const initialState = {
    permissionsData: [],
    loading: false,
};

export const fetchPermissions = createAsyncThunk(
    "permission/fetchPermissions",
    async ({roleId, scope}, { rejectWithValue }) => {
        try {
            const [allPermsRes, assignedPermsRes] = await Promise.all([
                privateRequest.get("/api/permissions"),
                privateRequest.get(`/api/role-permissions/role/${roleId}`),
            ]);

            const allPermissions = allPermsRes?.data?.data || [];
            const assignedPermissions = assignedPermsRes?.data?.data || [];

            const permissionType = scope === "GLOBAL" ? "Admin" : "Basic";

            const filteredPermissions = allPermissions.filter(
                (perm) => perm.type === permissionType
            );

            const assignedIds = new Set();

            assignedPermissions
                .filter((item) => item.type === permissionType)
                .forEach((item) => {
                    item.modules?.forEach((module) => {
                        module.permissions?.forEach((permission) => {
                            if (permission.isallowed) {
                                assignedIds.add(permission.id);
                            }
                        });
                    });
                });

            const modulesMap = new Map();

            filteredPermissions.forEach((perm) => {

                const moduleName = perm.module || "General";

                if (!modulesMap.has(moduleName)) {
                    modulesMap.set(moduleName, []);
                }

                modulesMap.get(moduleName).push({
                    ...perm,
                    activity: perm.activity || perm.description,
                    isallowed: assignedIds.has(perm.id),
                });

            });

            return Array.from(modulesMap.entries()).map(
                ([module, permissions]) => ({
                    module,
                    permissions,
                })
            );
        } catch (error) {
            return rejectWithValue(error.response?.data);
        }
    }
);

export const updateRolePermission = createAsyncThunk(
  "permission/updateRolePermission",
  async ({ roleId, permissionIds, scope }, { dispatch, rejectWithValue }) => {
    try {
      await privateRequest.put("/api/role-permissions", {
        roleId,
        permissionIds: permissionIds,
      });
      
      return permissionIds;
    } catch (error) {
      console.error("Error updating permission:", error);
      return rejectWithValue(error.response?.data);
    }
  }
);

const permissionSlice = createSlice({
    name: "permission",
    initialState,
    reducers: {
        setLocalPermission: (state, action) => {
            const { permissionId, checked } = action.payload;
            state.permissionsData = state.permissionsData.map((module) => ({
                ...module,
                permissions: module.permissions.map((perm) =>
                    perm.id === permissionId
                        ? { ...perm, isallowed: checked }
                        : perm
                ),
            }));
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPermissions.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchPermissions.fulfilled, (state, action) => {
                state.loading = false;
                state.permissionsData = action.payload;
            })
            .addCase(fetchPermissions.rejected, (state) => {
                state.loading = false;
            });
    },
});

export const { setLocalPermission } = permissionSlice.actions;
export default permissionSlice.reducer;
